import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";

/** Bullet keys, in reading order. The suffix after the last dot is also the per-bullet test id. */
const FALLBACK_BULLET_KEYS = ["reason", "coverage", "unmapped", "gross_hours", "not_submission"] as const;

type Props = {
	/**
	 * `fallback` — confirmed: the department has no PBJ mirror and every number on the tab came from
	 * the staffing schedule.
	 * `unknown` — the worked-source query FAILED. The seven data routes each resolve their own source
	 * server-side, so the grids may still be showing schedule-derived, gross, unmapped hours. Rendering
	 * nothing here would present those as the CMS basis with no disclosure at all, which is the wrong
	 * failure direction on a compliance surface — so the banner says it could not confirm, instead.
	 */
	variant: "fallback" | "unknown";
};

/**
 * DAT-810 — disclosure banner for a department whose PBJ Audit numbers were read from the staffing
 * schedule rather than payroll-derived PBJ hours.
 *
 * It sits ABOVE the sub-tab bar in `AuditTab`, so it is visible on every sub-tab. That placement is
 * deliberate and shapes the copy: it also shows on **Excluded from PBJ**, which genuinely has nothing
 * to fall back to, so the last bullet says so explicitly rather than letting the banner imply that
 * every tab now has data.
 *
 * The copy states what the backend actually does. In particular it says these numbers cover only the
 * staff you SCHEDULE in InHouse — never "nursing staff only": the reader
 * (`get_finalized_actual_shifts_overlapping_window`) applies no job or PPD filter, so a facility that
 * schedules Housekeepers, Cooks or Dietary Aides in InHouse has their hours counted, correctly (CMS
 * PBJ job codes span non-nursing categories). "Typically" is what keeps the named list true.
 *
 * Reuses `SystemBanner` (tone `warning`) exactly as `PredictionTab/MissingJobsWarning` does — no new
 * component, no colour of its own. The description is a `<ul>`, so it needs
 * `descriptionComponent="div"`: a list inside the banner's default `<p>` is invalid nesting and the
 * browser silently closes the paragraph around it.
 */
export const ActualsFallbackWarning: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();

	if (variant === "unknown") {
		return (
			<SystemBanner
				tone="warning"
				icon={<WarningAmberOutlinedIcon />}
				title={t("pbj_model.audit.actuals_fallback.unknown.title")}
				description={t("pbj_model.audit.actuals_fallback.unknown.body")}
				dataTestId="pbj-model-audit-worked-source-unknown-warning"
			/>
		);
	}

	return (
		<SystemBanner
			tone="warning"
			icon={<WarningAmberOutlinedIcon />}
			title={t("pbj_model.audit.actuals_fallback.title")}
			description={
				<Box
					component="ul"
					sx={{
						m: 0,
						pl: 2.25,
						display: "flex",
						flexDirection: "column",
						gap: 0.375,
					}}
				>
					{FALLBACK_BULLET_KEYS.map((key) => (
						<Typography component="li" key={key} sx={{ fontSize: "inherit", lineHeight: 1.45 }} data-testid={`pbj-model-audit-actuals-fallback-${key}`}>
							{t(`pbj_model.audit.actuals_fallback.${key}`)}
						</Typography>
					))}
				</Box>
			}
			descriptionComponent="div"
			dataTestId="pbj-model-audit-actuals-fallback-warning"
		/>
	);
};
